/**
 * User-provided AI provider settings for the local-first audio pipeline
 * (transcribe + structure actions). Module-scope device-local signals
 * so the settings UI and the action handlers share the same reactive state.
 *
 * Precedent: `storage.nftstorage.api_key` in src/ipfs/storage.ts — API keys
 * live per device, never in the applog thread. Backed by localStorage plus a
 * Tauri store mirror (Android webview localStorage doesn't survive restarts —
 * see src/data/device-settings.ts).
 */

import { useDeviceSetting } from '../data/device-settings'

export const DEFAULT_TRANSCRIBE_MODEL = 'voxtral-mini-latest'
// medium over large: mistral-medium 3.5 outperforms large 3 on structuring (manu, 2026-07-10)
export const DEFAULT_STRUCTURE_MODEL = 'mistral-medium-latest'

export const [mistralApiKey, setMistralApiKey] = useDeviceSetting('note3.ai.mistralApiKey', '')
export const [transcribeModel, setTranscribeModel] = useDeviceSetting('note3.ai.transcribeModel', '')
export const [structureModel, setStructureModel] = useDeviceSetting('note3.ai.structureModel', '')

export const effectiveTranscribeModel = () => transcribeModel().trim() || DEFAULT_TRANSCRIBE_MODEL
export const effectiveStructureModel = () => structureModel().trim() || DEFAULT_STRUCTURE_MODEL

/** Error thrown when an AI action needs an API key that isn't configured. */
export class MissingApiKeyError extends Error {
	constructor() {
		super('No Mistral API key configured — add one in Settings → AI')
	}
}

export function requireMistralApiKey(): string {
	const key = mistralApiKey().trim()
	if (!key) throw new MissingApiKeyError()
	return key
}
